import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { newAuthService } from "Service/Axios";
import useAuth from "Context/useAuth";
import { getStudentAlertDetailsUrl, updateStudentOfferUrl } from "Constant/api";
import { formatDateWithFullMonthTime, viewPdfFileNewWindow } from "Utils";
import { Validator } from "Utils/validator";

export const AlertDetails = ({ show, toggle, alertId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [alertDetails, setAlertDetails] = useState(null);
  const [actionType, setActionType] = useState("");

  const [formData, setFormData] = useState({
    fullName: "",
    dateTime: "",
    rejectionType: "",
    rejectionReason: "",
    accept: false,
  });

  const [errMsg, setErrMsg] = useState({
    rejectionType: "",
    rejectionReason: "",
  });

  const { fullName, dateTime, rejectionType, rejectionReason, accept } =
    formData;

  // On Change
  const handleChange = (e) => {
    if (e.target.type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      }));
    }

    if (e.target.name === "rejectionType") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          rejectionType: t(
            "common.messages.error.alertDetails.rejectionType.blank"
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          rejectionType: "",
        }));
      }
    }

    if (e.target.name === "rejectionReason") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          rejectionReason: t(
            "common.messages.error.alertDetails.rejectionReason.blank"
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          rejectionReason: "",
        }));
      }
    }
  };

  // Listing
  const getAlertDetails = async () => {
    try {
      const payload = { gid: alertId };
      const response = await newAuthService(
        getStudentAlertDetailsUrl,
        "POST",
        payload
      );
      if (response.status) {
        setAlertDetails(response.data);
      } else {
        setAlertDetails(null);
      }
    } catch {
      toast.error(t("common.messages.notification.failed.serverError"));
    }
  };

  useEffect(() => {
    getAlertDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertId]);

  // Utils
  const onViewOfferLetterPdf = (url) => {
    viewPdfFileNewWindow(url);
  };

  // Validation
  const getValidation = () => {
    return new Promise((resolve) => {
      if (actionType === "Reject") {
        if (Validator.text(rejectionType) && Validator.text(rejectionReason)) {
          resolve(true);
        } else {
          if (!Validator.text(rejectionType)) {
            setErrMsg((prev) => ({
              ...prev,
              rejectionType: t(
                "common.messages.error.alertDetails.rejectionType.blank"
              ),
            }));
          }

          if (!Validator.text(rejectionReason)) {
            setErrMsg((prev) => ({
              ...prev,
              rejectionReason: t(
                "common.messages.error.alertDetails.rejectionReason.blank"
              ),
            }));
          }

          resolve(false);
        }
      } else {
        resolve(true);
      }
    });
  };

  // Submit & Reset
  const onResetAction = () => {
    setActionType("");
    setFormData({
      fullName: "",
      dateTime: "",
      rejectionType: "",
      rejectionReason: "",
      accept: false,
    });
    setErrMsg({
      rejectionType: "",
      rejectionReason: "",
    });
  };

  const onTriggerAction = (type) => {
    // if (type === "Accept") {
    //     setActionType(type);
    // }
    // if (type === "Reject") {
    // }
    if (type === "ActionForm") {
      navigate(alertDetails?.formPath);
    } else {
      setActionType(type);
      if (user) {
        setFormData((prev) => ({
          ...prev,
          fullName: user.name,
          dateTime: formatDateWithFullMonthTime(new Date()),
        }));
      }
    }
  };

  const onSubmitAction = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      try {
        const payload = {
          gid: alertId,
          name: fullName,
          date: dateTime,
        };
        if (actionType === "Accept") {
          payload.accept = true;
          payload.termCondition = true;
        }
        if (actionType === "Reject") {
          payload.reject = true;
          payload.rejectionType = rejectionType;
          payload.rejectionReason = rejectionReason;
        }
        const response = await newAuthService(
          updateStudentOfferUrl,
          "POST",
          payload
        );
        if (response.status) {
          toggle();
          toast.success(
            t(
              `common.messages.notification.success.${
                actionType === "Accept" ? "acceptOffer" : "rejectOffer"
              }`
            )
          );
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error(
          t("common.messages.notification.failed.somethingWentWrong")
        );
      }
    }
  };

  return (
    <Modal
      className="text-start fade right modal-slider"
      show={show}
      onHide={toggle}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton className="rounded-0">
        <Modal.Title className="d-flex align-items-center column-gap-2">
          {(actionType === "Accept" || actionType === "Reject") && (
            <button
              className="flex-auto w-8 p-0 d-flex justify-content-start align-items-center text-grey-400 border-0 bg-transparent"
              onClick={onResetAction}
            >
              <i className="bx bx-arrow-back"></i>
            </button>
          )}
          {alertDetails?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-grey-500">
        {actionType === "Accept" || actionType === "Reject" ? (
          <div className="p-4">
            <div className="mb-4">
              <label className="form-label" htmlFor="fullName">
                {t("common.label.name")}
              </label>
              <input
                type="text"
                className="form-control"
                id="fullName"
                placeholder={t("common.placeHolder.fullName")}
                name="fullName"
                value={fullName}
                onChange={handleChange}
                disabled
              />
            </div>
            <div className="mb-4">
              <label className="form-label" htmlFor="dateTime">
                {t("common.label.dateTime")}
              </label>
              <input
                type="text"
                className="form-control"
                id="dateTime"
                placeholder={t("common.placeHolder.dateTime")}
                name="dateTime"
                value={dateTime}
                onChange={handleChange}
                disabled
              />
            </div>

            {actionType === "Reject" && (
              <>
                <div className="mb-4">
                  <label className="form-label" htmlFor="rejectionType">
                    {t("common.label.rejectionType")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="rejectionType"
                    placeholder={t("common.placeHolder.rejectionType")}
                    name="rejectionType"
                    value={rejectionType}
                    onChange={handleChange}
                  />
                  {errMsg.rejectionType && (
                    <p className="error">{errMsg.rejectionType}</p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="form-label" htmlFor="rejectionReason">
                    {t("common.label.rejectionReason")}
                  </label>
                  <textarea
                    type="text"
                    className="form-control"
                    rows={4}
                    id="rejectionReason"
                    placeholder={t("common.placeHolder.rejectionReason")}
                    name="rejectionReason"
                    value={rejectionReason}
                    onChange={handleChange}
                  ></textarea>
                  {errMsg.rejectionReason && (
                    <p className="error">{errMsg.rejectionReason}</p>
                  )}
                </div>
              </>
            )}

            {actionType === "Accept" && (
              <div className="mb-4 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="accept"
                  name="accept"
                  value={accept}
                  onChange={handleChange}
                />
                {alertDetails?.footerText1 && (
                  <label className="form-check-label" for="accept">
                    {alertDetails?.footerText1}{" "}
                    <a href={alertDetails?.termConditionURL} target="_blank">
                      {alertDetails?.footerlinkText}
                    </a>{" "}
                    {alertDetails?.footerText2}
                  </label>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="p-3 d-flex flex-column gap-3">
            {alertDetails?.offerLetter && alertDetails?.offerLetter !== "" && (
              <div>
                <a
                  className="pdf-box rounded-lg border overflow-hidden"
                  href={alertDetails?.pdfURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  // onClick={() => onViewOfferLetterPdf(alertDetails?.pdfURL)}
                >
                  {alertDetails?.pdfSnippet && (
                    <div className="pdf-img">
                      <img
                        src={alertDetails?.pdfSnippet}
                        alt="PDF"
                        className="w-100 object-fit-cover"
                        height={100}
                      />
                    </div>
                  )}
                  <div className="px-3 py-2 d-flex align-items-center gap-3 text-slate-500 border-top border-slate-100 bg-white">
                    <span className="text-sm font-medium">
                      {alertDetails?.offerLetter}
                    </span>
                    <i className="bx bxs-file-pdf bx-sm ms-auto"></i>
                  </div>
                </a>
              </div>
            )}

            {alertDetails?.textType === "html" ? (
              alertDetails?.htmlText && (
                <div
                  dangerouslySetInnerHTML={{ __html: alertDetails?.htmlText }}
                />
              )
            ) : (
              <div className="text-sm">
                <p>{alertDetails?.text}</p>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="rounded-0 text-slate-500">
        <div className="px-3 pb-3 d-flex gap-2 border-bottom border-slate-100 bg-white position-sticky top-0 w-100">
          {actionType !== "Reject" && alertDetails?.button1 && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() =>
                actionType === "Accept"
                  ? onSubmitAction()
                  : onTriggerAction(alertDetails?.button1Type)
              }
              disabled={actionType === "Accept" && !accept}
            >
              {alertDetails?.button1}
            </button>
          )}
          {actionType !== "Accept" && alertDetails?.button2 && (
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() =>
                actionType === "Reject"
                  ? onSubmitAction()
                  : onTriggerAction(alertDetails?.button2Type)
              }
            >
              {alertDetails?.button2}
            </button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};
